/**
 * Configuração de URLs para assets públicos (imagens, vídeos, fontes)
 * 
 * Em desenvolvimento: usa caminhos relativos (servidos pelo webpack dev server)
 * Em produção: usa S3 bucket URL via REACT_APP_PUBLIC_ASSETS_URL
 * 
 * Uso:
 *   import { getAssetUrl, getImageUrl, getVideoUrl } from '../config/assets';
 *   
 *   <img src={getImageUrl('logo.png')} />
 *   <video src={getVideoUrl('background.mp4')} />
 */

// URL base para assets públicos
// Em produção: https://bucket-name.s3.region.amazonaws.com/public
// Em desenvolvimento: vazio (usa caminhos relativos)
const PUBLIC_ASSETS_URL = process.env.REACT_APP_PUBLIC_ASSETS_URL || '';

// Remove barra final se existir
const ASSETS_BASE = PUBLIC_ASSETS_URL.replace(/\/$/, '');

/**
 * Retorna a URL completa para um asset
 * @param path - Caminho do asset (ex: '/images/logo.png' ou 'images/logo.png')
 */
export const getAssetUrl = (path: string): string => {
  // Normaliza o path removendo barra inicial se existir
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  
  // Em desenvolvimento (ASSETS_BASE vazio), retorna o path relativo
  // Em produção, concatena com a URL do S3
  return ASSETS_BASE ? `${ASSETS_BASE}${normalizedPath}` : normalizedPath;
};

/**
 * Retorna a URL para uma imagem na pasta /images/
 * @param filename - Nome do arquivo (ex: 'logo.png')
 */
export const getImageUrl = (filename: string): string => {
  return getAssetUrl(`/images/${filename}`);
};

/**
 * Retorna a URL para um vídeo na pasta /videos/
 * @param filename - Nome do arquivo (ex: 'background.mp4')
 */
export const getVideoUrl = (filename: string): string => {
  return getAssetUrl(`/videos/${filename}`);
};

/**
 * Retorna a URL para uma fonte na pasta /fonts/
 * @param filename - Nome do arquivo (ex: 'Avenir.ttf')
 */
export const getFontUrl = (filename: string): string => {
  return getAssetUrl(`/fonts/${filename}`);
};

/**
 * Verifica se está usando S3 para assets
 */
export const isUsingS3Assets = (): boolean => {
  return Boolean(ASSETS_BASE);
};

/**
 * Lista de chaves de parâmetros que contêm caminhos de assets
 * e precisam ser convertidos para URLs do S3
 */
const ASSET_PARAMETER_KEYS: Record<string, 'image' | 'video' | 'font' | 'asset'> = {
  // Imagens
  'logo_evento': 'asset',
  'background_image': 'asset',
  'corner_image': 'asset',
  // Vídeos
  'photowall_background_video': 'asset',
  'background_video': 'asset',
  // Adicione mais chaves conforme necessário
};

/**
 * Processa parâmetros vindos da API e converte caminhos de assets para URLs do S3
 * 
 * @param parameters - Objeto com parâmetros da API (Record<string, string>)
 * @returns Parâmetros com URLs de assets convertidas
 * 
 * Exemplo:
 *   Input:  { "logo_evento": "/images/logo.png", "center_text": "Hello" }
 *   Output: { "logo_evento": "https://bucket.s3.../public/images/logo.png", "center_text": "Hello" }
 */
export const processAssetParameters = (parameters: Record<string, string>): Record<string, string> => {
  const processed: Record<string, string> = { ...parameters };
  
  for (const [key, value] of Object.entries(parameters)) {
    // Verificar se é uma chave conhecida de asset
    if (ASSET_PARAMETER_KEYS[key]) {
      // Converter para URL do S3 (getAssetUrl lida com caminhos relativos)
      processed[key] = getAssetUrl(value);
    } 
    // Detectar automaticamente caminhos que parecem ser assets
    else if (value && typeof value === 'string') {
      // Padrões de caminhos de assets
      if (value.match(/^\/?images\//i) || value.match(/^\/?videos\//i) || value.match(/^\/?fonts\//i)) {
        processed[key] = getAssetUrl(value);
      }
    }
  }
  
  return processed;
};

/**
 * Exporta a URL base para casos especiais
 */
export const ASSETS_BASE_URL = ASSETS_BASE;

export default {
  getAssetUrl,
  getImageUrl,
  getVideoUrl,
  getFontUrl,
  isUsingS3Assets,
  processAssetParameters,
  ASSETS_BASE_URL,
};
